import Paper from "@mui/material/Paper";
import { useCallback, useState } from "react";
import ConfirmButton from "../buttons/ConfirmButton";
import { Stack, Typography } from "@mui/material";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import DoneIcon from "@mui/icons-material/Done";
import {
  OrderDirection,
  UpgradeableProposal,
  UpgradeableProposal_OrderBy,
} from "../../../../../apis/__generated__/graphql";
import RevokeProposalButton from "../buttons/RevokeProposalButton";
import ExecuteButton from "../../../../owner/proposals/upgrade/buttons/ExecuteButton";
import ProposalStatusChip from "../../../../shared/ProposalStatusChip";
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { GET_UPGRADABLE_PROPOSALS } from "../../../../../libraries/queries/proposals";
import { ProposalStatus } from "../../../../../libraries/types/proposal";
import { useQuery } from "@apollo/client";

export interface UpgradeProposalTableProps {
  proposals: UpgradeableProposal[];
  loading: boolean;
  refetch: () => void;
}

export default function UpgradeProposalTable() {
  const [selectedRow, setSelectedRow] = useState<
    UpgradeableProposal | undefined
  >(undefined);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>();

  const {
    data: proposalData,
    refetch,
    loading,
  } = useQuery(GET_UPGRADABLE_PROPOSALS, {
    variables: {
      first: paginationModel.pageSize,
      skip: paginationModel.pageSize * paginationModel.page,
      orderDirection:
        sortModel?.[0]?.sort === "asc"
          ? OrderDirection.Asc
          : OrderDirection.Desc,
      orderBy: sortModel?.[0]?.field as UpgradeableProposal_OrderBy,

      where: {
        status_in: [ProposalStatus.Pending, ProposalStatus.Approved],
      },
    },
  });

  const proposals = (proposalData?.upgradeableProposals ||
    []) as UpgradeableProposal[];

  const onPaginationModelChange = useCallback<
    NonNullable<DataGridProProps["onPaginationModelChange"]>
  >((model) => {
    setPaginationModel(model);
  }, []);

  const onSortModelChange = useCallback<
    NonNullable<DataGridProProps["onSortModelChange"]>
  >(
    (model) => {
      setSortModel(model);
    },
    [setSortModel]
  );

  const columns: GridColDef<UpgradeableProposal>[] = [
    {
      field: "newImplementation",
      headerName: "New Implementation",
      flex: 0.3,
      minWidth: 120,
    },
    {
      field: "confirms",
      headerName: "Confirms",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => {
        const numberOfConfirmations =
          (row.ownerApproved ? 1 : 0) + (row.keyKeeperApproved ? 1 : 0);

        return `${numberOfConfirmations}/2`;
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      renderCell: ({ value }) => <ProposalStatusChip status={value} />,
    },
    {
      field: "confirmations",
      headerName: "Confirmations",
      width: 200,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Stack direction="column">
            <Typography fontSize={14} display="flex">
              Owner:{" "}
              {row.ownerApproved ? (
                <DoneIcon fontSize="small" />
              ) : (
                <HourglassTopIcon fontSize="small" />
              )}
            </Typography>
            <Typography fontSize={14} display="flex">
              Key Keeper:{" "}
              {row.keyKeeperApproved ? (
                <DoneIcon fontSize="small" />
              ) : (
                <HourglassTopIcon fontSize="small" />
              )}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      width: 150,
      getActions: ({ row }) => {
        const numberOfConfirmations =
          (row.ownerApproved ? 1 : 0) + (row.keyKeeperApproved ? 1 : 0);

        return [
          <ConfirmButton
            key="Confirm"
            disabled={row.ownerApproved}
            onSelectedRow={() => setSelectedRow(row)}
            onUnselectedRow={() => setSelectedRow(undefined)}
            row={selectedRow}
            refetch={refetch}
          />,
          <ExecuteButton
            key="Execute"
            disabled={numberOfConfirmations < 2}
            onSelectedRow={() => setSelectedRow(row)}
            onUnselectedRow={() => setSelectedRow(undefined)}
            row={selectedRow}
            refetch={refetch}
          />,
          <RevokeProposalButton
            key="Revoke"
            disabled={false}
            onSelectedRow={() => setSelectedRow(row)}
            onUnselectedRow={() => setSelectedRow(undefined)}
            row={selectedRow}
            refetch={refetch}
          />,
        ];
      },
    },
  ];

  return (
    <Paper>
      <DataGridPro
        sx={{
          ".MuiTablePagination-displayedRows": {
            display: "none",
          },
        }}
        pagination
        rows={proposals}
        columns={columns}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        rowCount={
          proposals.length === 10
            ? paginationModel.pageSize * (paginationModel.page + 1) + 1
            : paginationModel.pageSize * (paginationModel.page + 1)
        }
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        loading={loading}
        autoHeight
        disableColumnMenu={true}
        rowHeight={100}
        paginationModel={paginationModel}
        sortModel={sortModel}
        onPaginationModelChange={onPaginationModelChange}
        onSortModelChange={onSortModelChange}
      />
    </Paper>
  );
}
